import {
  Badge,
  Button,
  Flex,
  FormLabel,
  Heading,
  IconButton,
  Input,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
  Spinner,
  Box,
  Link as ChakraLink,
  InputGroup,
  InputAddon,
  Spacer,
  Select
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import Paginator from '../../../components/paginator'
import UrlShortenerService, { IUrlShortener } from '../../../services/UrlShortener/UrlShortenerService'
import UrlShortenerDeleteDialog from './UrlShortenerDeleteDialog'
import UrlShortenerFormModal from './UrlShortenerFormModal'
import UrlShortenerQrCodeModal from './UrlShortenerQrCodeModal'
import { UrlShortenerViewModal } from './UrlShortenerViewModal'

const formatOriginalUrl = (url: string) => {
  if (url.length <= 35) return url
  return url.slice(0, 15) + '...' + url.slice(-15)
}

export default function UrlShortenerPage() {
  const [urls, setUrls] = useState<IUrlShortener[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [filters, setFilters] = useState({
    length: 10,
    search: { value: '' },
    created_at: {
      from: '',
      to: ''
    }
  })
  const [loading, setLoading] = useState(true)
  const [showFormModal, setShowFormModal] = useState(false)
  const [showingRecord, setShowingRecord] = useState<number | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: number | null }>({ open: false, id: null })
  const [qrModal, setQrModal] = useState<{ open: boolean; url: string; title?: string }>({
    open: false,
    url: '',
    title: ''
  })
  const toast = useToast()

  const fetchUrls = async () => {
    setLoading(true)
    try {
      const data = await UrlShortenerService.list(
        filters.search.value,
        page,
        filters.length,
        filters.created_at.from,
        filters.created_at.to
      )
      setTotalPages(Math.ceil(data.recordsFiltered / filters.length))
      setUrls(data.data)
    } catch (e) {
      toast({ title: 'Erro ao carregar URLs', status: 'error' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUrls()
  }, [page])

  useEffect(() => {
    !showingRecord && fetchUrls()
  }, [showingRecord])

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchUrls()
    }, 400)
    return () => clearTimeout(delayDebounce)
  }, [filters.search.value, filters.created_at.from, filters.created_at.to, filters.length])

  const handleDelete = async (id: number) => {
    setDeleteDialog({ open: true, id })
  }

  const confirmDelete = async () => {
    if (!deleteDialog.id) return
    try {
      await UrlShortenerService.delete(deleteDialog.id)
      toast({ title: 'URL removida com sucesso', status: 'success' })
      fetchUrls()
    } catch (e) {
      toast({ title: 'Erro ao remover URL', status: 'error' })
    } finally {
      setDeleteDialog({ open: false, id: null })
    }
  }

  const getFullShortUrl = (short_path: string) => `https://abra.com.br/short/${short_path}`

  return (
    <Box>
      <Heading as="h1" size="lg" mb={10} textTransform="uppercase">
        Encurtador de URL
      </Heading>

      <div className="row mb-4">
        <div className="col-sm-4 col-md-6 col-lg-6 col-xl-8"></div>
        <div className="col-12 col-sm-8 col-md-6 col-lg-6 col-xl-4">
          <FormLabel htmlFor="date-start" fontSize="sm" fontWeight="normal">
            Filtrar cliques
          </FormLabel>
          <InputGroup display="flex" alignItems="center" size="sm">
            <Input
              type="date"
              size="sm"
              variant="outline"
              id="date-start"
              value={filters.created_at.from}
              onChange={(e) => setFilters({ ...filters, created_at: { ...filters.created_at, from: e.target.value } })}
              max={filters.created_at.to}
              min="2025-07-07"
            />
            <InputAddon>Até</InputAddon>
            <Input
              type="date"
              size="sm"
              variant="outline"
              id="date-end"
              value={filters.created_at.to}
              onChange={(e) => setFilters({ ...filters, created_at: { ...filters.created_at, to: e.target.value } })}
              min={filters.created_at.from}
            />
          </InputGroup>
        </div>
      </div>
      <Flex mb="4">
        <Flex flex="1" alignItems="center">
          <FormLabel htmlFor="per-page" fontSize="sm" fontWeight="normal">
            Mostrar
          </FormLabel>
          <Select
            size="sm"
            variant="outline"
            id="per-page"
            w="75px"
            mr="2"
            onChange={(e) => setFilters({ ...filters, length: parseInt(e.target.value) })}
            value={filters.length}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Select>
          <FormLabel htmlFor="per-page" fontSize="sm" fontWeight="normal">
            resultados por página
          </FormLabel>
        </Flex>
        <Spacer />
        <Flex flex="1" alignContent="center" justifyContent="flex-end">
          <FormLabel htmlFor="search" fontSize="sm" fontWeight="normal">
            Pesquisar
          </FormLabel>
          <Input
            type="search"
            size="sm"
            variant="outline"
            id="search"
            w="200px"
            onChange={(e) => setFilters({ ...filters, search: { value: e.target.value } })}
            value={filters.search.value}
          />
        </Flex>
      </Flex>
      <TableContainer>
        <Table fontSize="smaller" padding="0" mt="5">
          <Thead>
            <Tr>
              <Th p={1}>Título</Th>
              <Th p={1}>URL Curta</Th>
              <Th p={1}>URL Original</Th>
              <Th p={1}>Acessos</Th>
              <Th p={1}>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td p={1} colSpan={5} textAlign="center">
                  <Spinner size="md" />
                </Td>
              </Tr>
            ) : urls.length === 0 ? (
              <Tr>
                <Td p={1} colSpan={5} textAlign="center">
                  Nenhuma URL cadastrada.
                </Td>
              </Tr>
            ) : (
              urls.map((url) => (
                <Tr key={url.id}>
                  <Td p={1}>{url.title}</Td>
                  <Td p={1}>
                    <ChakraLink
                      href={getFullShortUrl(url.short_path)}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="blue.500"
                    >
                      {getFullShortUrl(url.short_path)}
                    </ChakraLink>
                  </Td>
                  <Td p={1} title={url.original_url}>
                    {formatOriginalUrl(url.original_url)}
                  </Td>
                  <Td p={1}>
                    <Badge colorScheme="purple">{url.period_clicks}</Badge>
                  </Td>
                  <Td p={1}>
                    <Tooltip label="QR Code">
                      <IconButton
                        aria-label="QR Code"
                        icon={<img src="/img/icons/qr-code.png" alt="QR Code" style={{ width: 18, height: 18 }} />}
                        size="xs"
                        colorScheme="gray"
                        mr={2}
                        onClick={() => setQrModal({ open: true, url: url.short_path, title: url.title })}
                      />
                    </Tooltip>
                    <Tooltip label="Visualizar">
                      <IconButton
                        aria-label="Visualizar"
                        icon={<i className="lni lni-eye" />}
                        size="xs"
                        colorScheme="green"
                        mr={2}
                        onClick={() => setShowingRecord(url.id)}
                      />
                    </Tooltip>
                    <Tooltip label="Excluir">
                      <IconButton
                        aria-label="Excluir"
                        icon={<i className="lni lni-trash-can" />}
                        size="xs"
                        colorScheme="red"
                        onClick={() => handleDelete(url.id)}
                      />
                    </Tooltip>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
        }}
      />
      <Tooltip label="Adicionar URL curta" aria-label="Adicionar URL curta" placement="left">
        <Button
          colorScheme="magenta"
          position="fixed"
          bottom="20px"
          right="20px"
          borderRadius="full"
          width="70px"
          height="70px"
          onClick={() => setShowFormModal(true)}
        >
          <i className="lni lni-plus"></i>
        </Button>
      </Tooltip>
      <UrlShortenerFormModal isOpen={showFormModal} onClose={() => setShowFormModal(false)} onCreated={fetchUrls} />
      <UrlShortenerDeleteDialog
        isOpen={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
        onDelete={confirmDelete}
      />
      <UrlShortenerQrCodeModal
        isOpen={qrModal.open}
        onClose={() => setQrModal({ open: false, url: '', title: '' })}
        url={qrModal.url}
        title={qrModal.title}
      />
      {showingRecord && <UrlShortenerViewModal id={showingRecord} onClose={() => setShowingRecord(null)} />}
    </Box>
  )
}
